import React, { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import ResponsiveImage from 'components/Common/ResponsiveImage'
import HeadData from 'components/Common/HeadData'
import config from 'constants/config'
import TextLink from 'LuxKit/TextLink'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const Root = styled.div`
  position: relative;
  overflow: hidden;
  color: ${props => props.theme.palette.neutral.default.eight};
  text-align: center;

  picture,
  img {
    height: calc(100vh - ${() => rem(config.header.height.lg)});
    object-fit: cover;
  }
`

const LargeHeading = styled(Heading)`
  font-size: 140px !important;
  line-height: 140px !important;
`

const Wrapper = styled(VerticalSpacer)`
  position: absolute;
  z-index: 1;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

interface Props {
  invalidRegion?: boolean
}

function Error404Page({ invalidRegion = false }: Props) {
  const intl = useIntl()
  const onBack = useCallback<React.MouseEventHandler<HTMLAnchorElement>>((event) => {
    event.preventDefault()
    window.history.back()
  }, [])

  const pageTitle = intl.formatMessage({
    id: 'WwWtM2d01r',
    defaultMessage: 'Page Not Found',
    description: 'Page title for the 404 error page shown in browser tab',
  })

  const pageDescription = intl.formatMessage({
    id: 'DnSIOCXQcs',
    defaultMessage: '404 - page not found',
    description: 'HTML meta description for the 404 error page on the 404 error page',
  })

  const imageAlt = intl.formatMessage({
    id: '1TxMAiYY+M',
    defaultMessage: '404, This page could not be found',
    description: 'Accessibility alt text for background image on 404 error page',
  })

  const regionUnavailableText = intl.formatMessage({
    id: 'vAneaEeR0B',
    defaultMessage: 'This page is not available in your region',
    description: 'Message shown when page is not available in the user’s region',
  })

  const pageNotFoundText = intl.formatMessage({
    id: 'q6WxCZWdSN',
    defaultMessage: 'Page not found',
    description: 'Message shown when the requested page does not exist',
  })

  return (
    <Root className="Error404Page T-Error404Page">
      <HeadData
        title={pageTitle}
        description={pageDescription}
      />
      <ResponsiveImage
        aria-hidden="true"
        alt={imageAlt}
        brightness={-60}
        mobileAspectRatio="8:9"
        tabletAspectRatio="4:3"
        desktopAspectRatio="4:3"
        largeDesktopAspectRatio="16:9"
        id="404-background"
      />
      <Wrapper data-testid="404-error-page" gap={16}>
        { invalidRegion &&
          <BodyText variant="large" colour="neutral-eight">{regionUnavailableText}</BodyText>}
        { !invalidRegion &&
          <>
            <LargeHeading variant="heading1" colour="neutral-eight">{intl.formatNumber(404)}</LargeHeading>
            <BodyText variant="large" colour="neutral-eight">{pageNotFoundText}</BodyText>
          </>}
        <BodyText variant="large">
          <FormattedMessage
            id="RIz1qtasWH"
            defaultMessage="Please go <backLink>Back</backLink> or head <homeLink>Home</homeLink>"
            description="Navigation help text with back and home links on 404 error page"
            values={{
              backLink: (chunks: React.ReactNode) => <TextLink colour="neutral-eight" onClick={onBack}>{chunks}</TextLink>,
              homeLink: (chunks: React.ReactNode) => <TextLink colour="neutral-eight" to="/">{chunks}</TextLink>,
            }}
          />
        </BodyText>
      </Wrapper>
    </Root>
  )
}

export default React.memo(Error404Page)
